// Schlage Home API client — thin wrappers around api.intrlock.io/schlage/*
// Backend lives in access-api Lambda. OAuth callback at /schlage/callback
// persists tokens to acs_systems.credentials_json keyed by state=<acs_system_id>.
import { apiOrigin } from '../utils/surface';

const BASE = apiOrigin();

// Public Schlage app config — safe to ship to browser. (Secret stays in Lambda.)
const SCHLAGE_CLIENT_ID = '1hv64i3m0gifpl194arj2k1o08';
const SCHLAGE_AUTHORIZE_URL = 'https://account.schlage.com/OAuth2/authorize';
// Allegion has this exact production callback registered for the Intrlock
// Schlage Home OAuth client. Keep it stable across the preview and live UI so
// the authorization screen matches the production flow; do not derive it from
// the current Shadow hostname (Allegion rejects that URI).
const SCHLAGE_REDIRECT_URI = 'https://api.intrlock.io/schlage/callback';

async function authed(path: string, init: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem('intrlock_token') || '';
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : '',
      ...(init.headers || {}),
    },
  });
  const text = await res.text();
  let data: any = text;
  if (text) { try { data = JSON.parse(text); } catch { /* keep text */ } }
  if (!res.ok) throw new Error(data?.error || data?.message || `${res.status}`);
  return data;
}

export interface SchlageAccount {
  id: string;
  status: string;
  name: string;
  has_token: boolean;
}

export interface SchlageLock {
  external_id: string;
  name: string;
  type: string;
  model?: string;
  serial_number?: string;
  firmware_version?: string;
  connected?: boolean;
  lock_state?: string;
  battery_state?: string;
  battery_percent?: number;
  last_seen?: string;
}

export async function getSchlageAccountForProperty(propertyId: string): Promise<SchlageAccount | null> {
  return authed(`/schlage/account?property_id=${encodeURIComponent(propertyId)}`);
}

export async function createSchlageAccount(propertyId: string): Promise<{ id: string }> {
  return authed('/schlage/account', {
    method: 'POST',
    body: JSON.stringify({ property_id: propertyId }),
  });
}

export async function listSchlageDevices(acsSystemId: string): Promise<SchlageLock[]> {
  return authed(`/schlage/devices?acs_system_id=${encodeURIComponent(acsSystemId)}`);
}

export async function unlockSchlageDevice(acsSystemId: string, deviceId: string): Promise<any> {
  return authed('/schlage/devices/unlock', {
    method: 'POST',
    body: JSON.stringify({ acs_system_id: acsSystemId, device_id: deviceId }),
  });
}

export async function lockSchlageDevice(acsSystemId: string, deviceId: string): Promise<any> {
  return authed('/schlage/devices/lock', {
    method: 'POST',
    body: JSON.stringify({ acs_system_id: acsSystemId, device_id: deviceId }),
  });
}

// ── Access codes (PINs) ─────────────────────────────────────────────────────

export interface SchlageAccessCode {
  accessCodeId?: string;
  AccessCodeId?: string;
  Name?: string;
  AccessCode?: string;
  Schedule?: { ScheduleType?: string; [k: string]: unknown };
  [k: string]: unknown;
}

export interface SchlageSchedule {
  ScheduleType: 'Always' | 'Temporary' | 'Recurring';
  StartTime?: string; // ISO for Temporary
  EndTime?: string;
  DaysOfWeek?: string[]; // for Recurring
  StartHour?: number;
  EndHour?: number;
}

export async function listSchlageAccessCodes(acsSystemId: string, deviceId: string): Promise<SchlageAccessCode[]> {
  const q = new URLSearchParams({ acs_system_id: acsSystemId, device_id: deviceId });
  return authed(`/schlage/access-codes?${q}`);
}

export async function mintSchlageAccessCode(acsSystemId: string, deviceId: string, code: string, name: string, schedule?: SchlageSchedule): Promise<any> {
  return authed('/schlage/access-codes', {
    method: 'POST',
    body: JSON.stringify({ acs_system_id: acsSystemId, device_id: deviceId, code, name, schedule }),
  });
}

export async function updateSchlageAccessCode(acsSystemId: string, deviceId: string, codeId: string, updates: { name?: string; code?: string; schedule?: SchlageSchedule }): Promise<any> {
  return authed('/schlage/access-codes', {
    method: 'PUT',
    body: JSON.stringify({ acs_system_id: acsSystemId, device_id: deviceId, code_id: codeId, ...updates }),
  });
}

export async function revokeSchlageAccessCode(acsSystemId: string, deviceId: string, codeId: string): Promise<any> {
  return authed('/schlage/access-codes', {
    method: 'DELETE',
    body: JSON.stringify({ acs_system_id: acsSystemId, device_id: deviceId, code_id: codeId }),
  });
}

export async function listSchlageEvents(acsSystemId: string, deviceId: string, fromIso?: string, toIso?: string): Promise<any> {
  const q = new URLSearchParams({ acs_system_id: acsSystemId, device_id: deviceId });
  if (fromIso) q.set('from', fromIso);
  if (toIso) q.set('to', toIso);
  return authed(`/schlage/events?${q}`);
}

// Build the Schlage authorize URL. state=<acs_systems.id> so the callback
// knows which row to populate.
export function buildSchlageAuthorizeUrl(state: string): string {
  const u = new URL(SCHLAGE_AUTHORIZE_URL);
  u.searchParams.set('response_type', 'code');
  u.searchParams.set('client_id', SCHLAGE_CLIENT_ID);
  u.searchParams.set('redirect_uri', SCHLAGE_REDIRECT_URI);
  u.searchParams.set('state', state);
  // Schlage requires scope=email openid per developer.allegion.com docs.
  // Without it, Cognito rejects with "Invalid scopes".
  u.searchParams.set('scope', 'email openid');
  return u.toString();
}

// Open OAuth popup. Resolves when the user returns (popup closes) AND the
// acs_systems row reaches status='active'. Rejects on timeout (5 min).
export async function authorizeSchlage(propertyId: string): Promise<string> {
  // Open synchronously while the click still carries user activation. Opening
  // after the account API calls causes Chrome/Safari to classify the popup as
  // unsolicited and silently block the Schlage login screen.
  const popup = window.open('about:blank', 'schlage-oauth', 'width=520,height=720');
  if (!popup) throw new Error('Popup blocked — allow popups for app.intrlock.io and retry');

  let existing: SchlageAccount | null;
  try {
    existing = await getSchlageAccountForProperty(propertyId);
  } catch (error) {
    try { popup.close(); } catch (_) { /* ignore */ }
    throw error;
  }
  if (existing?.has_token && existing.status === 'active') {
    try { popup.close(); } catch (_) { /* ignore */ }
    return existing.id;
  }

  let acsSystemId: string;
  try {
    ({ id: acsSystemId } = existing
      ? { id: existing.id }
      : await createSchlageAccount(propertyId));
  } catch (error) {
    try { popup.close(); } catch (_) { /* ignore */ }
    throw error;
  }

  const url = buildSchlageAuthorizeUrl(acsSystemId);
  popup.location.replace(url);

  const startedAt = Date.now();
  const timeoutMs = 5 * 60 * 1000;
  while (Date.now() - startedAt < timeoutMs) {
    await new Promise((r) => setTimeout(r, 2000));
    try {
      const acc = await getSchlageAccountForProperty(propertyId);
      if (acc?.id === acsSystemId && acc.has_token && acc.status === 'active') {
        try { popup.close(); } catch (_) { /* popup may already be closed */ }
        return acsSystemId;
      }
    } catch { /* keep polling */ }
    if (popup.closed && Date.now() - startedAt > 5000) {
      // Popup closed but token not received — verify one last time then bail
      const acc = await getSchlageAccountForProperty(propertyId).catch(() => null);
      if (acc?.has_token) return acc.id;
      throw new Error('Schlage authorization cancelled');
    }
  }
  throw new Error('Schlage authorization timed out');
}
